/**
 * Reverse geocoding utility using Nominatim (OpenStreetMap)
 * Returns structured address data from coordinates
 */

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/reverse';

/**
 * Performs reverse geocoding to get address from coordinates
 * @param {number} latitude - Latitude coordinate
 * @param {number} longitude - Longitude coordinate
 * @returns {Promise<Object>} Parsed address object
 */
const reverseGeocode = async (latitude, longitude) => {
  if (latitude == null || longitude == null) {
    throw new Error('Invalid coordinates');
  }

  const params = new URLSearchParams({
    lat: latitude.toString(),
    lon: longitude.toString(),
    format: 'json',
    addressdetails: '1',
    zoom: '18',
  });

  const response = await fetch(`${NOMINATIM_URL}?${params}`, {
    headers: {
      'Accept-Language': navigator.language || 'pt-BR',
    },
  });

  if (!response.ok) {
    throw new Error(`Geocoding failed: ${response.status}`);
  }

  const data = await response.json();

  if (!data || data.error) {
    throw new Error(data?.error || 'No results found');
  }

  const { address } = data;

  // Parse address components for Brazilian and international formats
  const result = {
    // Full formatted address
    address: formatStreetAddress(address),
    // City - try multiple fields (Nominatim varies by country)
    city: address.city
      || address.town
      || address.municipality
      || address.village
      || address.hamlet
      || address.suburb
      || '',
    // State/Province
    state: address.state
      || address.province
      || address.region
      || '',
    // Postal code
    postalCode: address.postcode || '',
    // Additional data that might be useful
    country: address.country || '',
    countryCode: address.country_code?.toUpperCase() || '',
    neighborhood: address.suburb
      || address.neighbourhood
      || address.quarter
      || '',
    // Raw display name from Nominatim
    displayName: data.display_name || '',
  };

  return result;
};

/**
 * Formats street address from Nominatim address components
 * @param {Object} address - Nominatim address object
 * @returns {string} Formatted street address
 */
const formatStreetAddress = (address) => {
  const parts = [];

  // Street name
  const street = address.road
    || address.street
    || address.pedestrian
    || address.footway
    || address.path
    || '';

  if (street) {
    // Include house number if available
    if (address.house_number) {
      parts.push(`${street}, ${address.house_number}`);
    } else {
      parts.push(street);
    }
  }

  // Neighborhood/suburb
  const neighborhood = address.suburb
    || address.neighbourhood
    || address.quarter
    || '';

  if (neighborhood && !parts.includes(neighborhood)) {
    parts.push(neighborhood);
  }

  return parts.join(' - ');
};

export default reverseGeocode;
